import { Plus, AlertTriangle } from 'lucide-react';
import { MealEditorCard } from './MealEditorCard';
import {
  MEAL_TYPES,
  DAY_LABELS,
  type PlanEditorMeal,
  type MealType,
} from '../lib/api';

interface Props {
  weekNumber: number;
  meals: PlanEditorMeal[];        // tutti i pasti del piano (cross-week, filtra qui)
  patientId: string | null;
  clipboard: PlanEditorMeal | null;
  onUpdateMeal: (clientId: string, next: PlanEditorMeal) => void;
  onRemoveMeal: (clientId: string) => void;
  onAddMeal: (dayOfWeek: number, mealType: MealType, weekOfPlan: number) => void;
  onAddVariant: (slotMeals: PlanEditorMeal[], dayOfWeek: number, mealType: MealType, weekOfPlan: number) => void;
  onCopyMeal: (meal: PlanEditorMeal) => void;
  onPasteMeal: (dayOfWeek: number, mealType: MealType, weekOfPlan: number) => void;
  /** Target kcal giornaliero per warning. */
  kcalDailyTarget?: number;
}

/**
 * Sprint 9.B — Vista 1 settimana, grid 7 giorni × 5 meal type.
 * Validation dietetica: warning su kcal/giorno fuori range fisiologico (1200-3500).
 */
export function PianoWeekTab({
  weekNumber,
  meals,
  patientId,
  clipboard,
  onUpdateMeal,
  onRemoveMeal,
  onAddMeal,
  onAddVariant,
  onCopyMeal,
  onPasteMeal,
  kcalDailyTarget,
}: Props) {
  // Filtra pasti di questa settimana (week_of_plan === weekNumber, oppure null = week 1)
  const weekMeals = meals.filter((m) => (m.week_of_plan ?? 1) === weekNumber);

  // Aggrega per giorno+slot
  const slotMap = new Map<string, PlanEditorMeal[]>();
  for (const m of weekMeals) {
    const key = `${m.day_of_week}-${m.meal_type}`;
    const arr = slotMap.get(key) || [];
    arr.push(m);
    slotMap.set(key, arr);
  }

  // Kcal per giorno (somma tutti gli items dei pasti non-fasting + non-variant-duplicati)
  // Per le varianti contiamo solo la prima (la A) come "preview".
  const kcalByDay = new Map<number, number>();
  for (let d = 1; d <= 7; d++) {
    let total = 0;
    for (const mt of MEAL_TYPES) {
      const slotMeals = slotMap.get(`${d}-${mt.value}`) || [];
      const variantAOrSingle = slotMeals.find((m) => !m.variant_label || m.variant_label === 'A') || slotMeals[0];
      if (variantAOrSingle && variantAOrSingle.is_fasting !== 1) {
        for (const it of variantAOrSingle.items) total += it.calories ?? 0;
      }
    }
    kcalByDay.set(d, total);
  }

  const aiContextFor = (mealType: MealType, dayOfWeek: number) => {
    const dayLabel = DAY_LABELS[dayOfWeek - 1];
    const parts = [
      `pasto: ${MEAL_TYPES.find((m) => m.value === mealType)?.label || mealType}`,
      `giorno: ${dayLabel}`,
      `settimana: ${weekNumber}`,
    ];
    if (kcalDailyTarget) parts.push(`target kcal/giorno: ${kcalDailyTarget}`);
    return parts.join(', ').slice(0, 180);
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 14,
      }}
    >
      {[1, 2, 3, 4, 5, 6, 7].map((d) => {
        const dayKcal = kcalByDay.get(d) || 0;
        const tooHigh = dayKcal > 3500;
        const tooLow = dayKcal > 0 && dayKcal < 1200;
        const targetDelta = kcalDailyTarget && dayKcal > 0 ? dayKcal - kcalDailyTarget : 0;

        return (
          <div
            key={d}
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--line)',
              borderRadius: 12,
              padding: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <div
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: 1.4,
                  color: 'var(--ink)',
                  textTransform: 'uppercase',
                }}
              >
                {DAY_LABELS[d - 1]}
              </div>
              <div
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 11,
                  fontWeight: 600,
                  color: tooHigh || tooLow ? 'var(--red)' : 'var(--mute)',
                  marginLeft: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {(tooHigh || tooLow) && <AlertTriangle size={11} />}
                {dayKcal > 0 ? `~${dayKcal} kcal` : '—'}
                {kcalDailyTarget && dayKcal > 0 && (
                  <span style={{ fontSize: 10, opacity: 0.7 }}>
                    ({targetDelta >= 0 ? '+' : ''}{targetDelta})
                  </span>
                )}
              </div>
            </div>

            {(tooHigh || tooLow) && (
              <div
                style={{
                  background: 'var(--red-soft, #fff0f0)',
                  border: '1px solid var(--red)',
                  borderRadius: 6,
                  padding: '6px 8px',
                  fontSize: 11,
                  color: 'var(--red)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <AlertTriangle size={11} />
                {tooHigh ? 'Kcal oltre 3500 — fuori range fisiologico' : 'Kcal sotto 1200 — restrizione severa'}
              </div>
            )}

            {MEAL_TYPES.map((mt) => {
              const slotMeals = slotMap.get(`${d}-${mt.value}`) || [];
              const existingLabels = new Set(
                slotMeals.map((m) => m.variant_label).filter((l): l is string => !!l)
              );
              const nextVariantLabel = ['A', 'B', 'C', 'D'].find((l) => !existingLabels.has(l)) || null;
              const isFastingSlot = slotMeals.some((m) => m.is_fasting === 1);
              const canAddVariant = slotMeals.length >= 1 && slotMeals.length < 4 && !isFastingSlot && !!nextVariantLabel;
              // Se è già il primo pasto, dobbiamo assicurare che esista variant_group
              // (lo fa la callback onAddVariant nel parent).

              return (
                <div key={mt.value} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: 1,
                      textTransform: 'uppercase',
                      color: 'var(--mute)',
                      fontFamily: 'var(--mono)',
                    }}
                  >
                    {mt.label}
                  </div>
                  {slotMeals.length === 0 ? (
                    <button
                      type="button"
                      onClick={() => onAddMeal(d, mt.value, weekNumber)}
                      style={{
                        border: '1px dashed var(--line)',
                        background: 'transparent',
                        color: 'var(--mute)',
                        padding: '8px 10px',
                        borderRadius: 8,
                        fontSize: 12,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        justifyContent: 'center',
                      }}
                    >
                      <Plus size={12} /> Aggiungi
                      {clipboard && (
                        <span
                          role="button"
                          tabIndex={0}
                          aria-label="Incolla appunto"
                          onClick={(e) => {
                            e.stopPropagation();
                            onPasteMeal(d, mt.value, weekNumber);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.stopPropagation();
                              onPasteMeal(d, mt.value, weekNumber);
                            }
                          }}
                          style={{
                            marginLeft: 4,
                            fontSize: 10.5,
                            background: 'var(--surface-2)',
                            padding: '2px 6px',
                            borderRadius: 4,
                            color: 'var(--ink-2)',
                            cursor: 'pointer',
                          }}
                        >
                          ⎘ paste
                        </span>
                      )}
                    </button>
                  ) : (
                    slotMeals.map((m) => (
                      <MealEditorCard
                        key={m.client_id}
                        meal={m}
                        patientId={patientId}
                        aiContext={aiContextFor(mt.value, d)}
                        onChange={(next) => onUpdateMeal(m.client_id, next)}
                        onRemove={() => onRemoveMeal(m.client_id)}
                        canAddVariant={canAddVariant}
                        nextVariantLabel={nextVariantLabel}
                        onAddVariant={() => onAddVariant(slotMeals, d, mt.value, weekNumber)}
                        onCopy={() => onCopyMeal(m)}
                        onPaste={() => onPasteMeal(d, mt.value, weekNumber)}
                        hasClipboard={!!clipboard}
                      />
                    ))
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
